import {
  defaultFilters as defaultOppFilters,
  type OpportunityFilterSet,
} from '@user/store/opportunity/filters'
import type { SalesFunnelFilterSet } from '@user/store/salesFunnelSummary/filters/typings'
import type { ISalesFunnelCard } from '@user/store/salesFunnelSummary/typings'

/**
 * Opportunity filters behind a Sales Funnel "Lost" count.
 *
 * The count is every opportunity in the Closed/Lost stage, so filter on
 * closedLost rather than inferring "lost" from sold-service status, which
 * misses Won -> Lost opportunities that keep a Completed status (WWM-18275).
 * Don't list funnel or stage IDs here: the URL would grow with the tenant's
 * funnel count until the request is rejected.
 *
 * Pass `funnel` for a single funnel card; omit it for the summary, which keeps
 * the summary's own funnel filter.
 */
export const buildLostOpportunityFilters = (
  funnelFilters: SalesFunnelFilterSet,
  funnel?: Pick<ISalesFunnelCard, 'salesFunnelId' | 'salesFunnelName'>,
): OpportunityFilterSet => ({
  ...defaultOppFilters,
  filters: {
    ...defaultOppFilters.filters,
    closed: {
      ...defaultOppFilters.filters.closed,
      value: 'empty',
    },
    closedLost: {
      ...defaultOppFilters.filters.closedLost,
      value: 'true',
    },
    salesFunnelId: {
      ...defaultOppFilters.filters.salesFunnelId,
      value: funnel
        ? {
            mode: 'any',
            options: [
              { label: funnel.salesFunnelName, value: funnel.salesFunnelId },
            ],
          }
        : funnelFilters.filters.salesFunnelIds.value,
    },
    closedDate: {
      ...defaultOppFilters.filters.closedDate,
      value: funnelFilters.filters.createdDate.value,
    },
    salesTeamIds: {
      ...defaultOppFilters.filters.salesTeamId,
      value: funnelFilters.filters.salesTeamIds.value,
    },
    assignee: {
      ...defaultOppFilters.filters.assignee,
      value: funnelFilters.filters.ownerIds.value,
    },
    type: {
      ...defaultOppFilters.filters.type,
      value: funnelFilters.filters.type.value,
    },
  },
})
